import React from 'react';
import PropTypes from 'prop-types';

import { Alert, Badge } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { toLower } from 'lodash';

import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import { post } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';

export default class CreateModal extends React.Component {
  static propTypes = {
    listings: PropTypes.arrayOf(PropTypes.shape({
      address: PropTypes.string,
      createVoucherPath: PropTypes.string,
      id: PropTypes.number,
      number: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      typeLabel: PropTypes.string,
    })),
    show: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onNewVoucher: PropTypes.func.isRequired,
  }

  state = { errors: [] };

  handleSubmit = () => {
    const { voucher } = this.state;

    if (voucher) {
      window.location = voucher.path;
    }
  }

  handleCreateVoucher = () => {
    const { onNewVoucher } = this.props;
    const listing = (this.props.listings.length > 1) ? this.state.listing : this.props.listings[0];

    this.setState({ errors: [], pendingRequest: true });

    post(listing.createVoucherPath)
      .then(({ voucher }) => {
        onNewVoucher(voucher);
        this.setState({ pendingRequest: false, voucher });
      })
      .catch((error) => {
        this.setState({
          errors: requestErrors(error, 'The voucher could not be created. Please try again.'),
          pendingRequest: false,
        });
      });
  }

  // Not while the create is on its way, so a late failure can't show up in the next opening.
  handleCancel = () => {
    if (this.state.pendingRequest) return;

    this.setState({
      errors: [],
      listing: null,
      voucher: null,
    });

    this.props.onCancel();
  }

  handleValueChange = (listingId) => {
    const { listings } = this.props;
    const listing = listings.find(listing => listing.id == listingId);

    this.setState({ listing });
  }

  render() {
    const { show } = this.props;

    return (
      <Modal name="create-modal" show={show} onHide={this.handleCancel}>
        <Modal.Header closeButton>
          New Voucher
        </Modal.Header>
        <React.Fragment>
          <Modal.Body>
            {this.renderBody()}
            <Errors className="mt-3" errors={this.state.errors} title={false} />
          </Modal.Body>
          <Modal.Footer>
            {this.state.voucher ? null : (
              <Button
                disabled={this.state.pendingRequest}
                label="Cancel"
                name="cancel-button"
                variant="ghost"
                onClick={this.handleCancel}
              />
            )}
            {this.renderSubmitButton()}
          </Modal.Footer>
        </React.Fragment>
      </Modal>
    );
  }

  renderBody() {
    const { listings } = this.props;
    const { listing, voucher } = this.state;

    if (listings == null) return;

    if (voucher) {
      return this.renderVoucherCreatedBody(voucher);
    } else if (listing && listing.voucher && listing.voucher.path) {
      return (
        <React.Fragment>
          {this.renderPickerBody(listings)}
          {this.renderVoucherExistWarning(listing.voucher)}
        </React.Fragment>
      );
    } else if (listings.length > 1) {
      return this.renderPickerBody(listings);
    } else if (listings[0].voucher && listings[0].voucher.path) {
      return this.renderVoucherExistWarning(listings[0].voucher);
    } else {
      return this.renderConfirmationBody(listings[0]);
    }
  }

  renderVoucherExistWarning({ oneLineLabel, path }) {
    return (
      <Alert className="d-flex gap-2 mt-3" variant="warning">
        <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
        <div>
          <div>A voucher for <strong>{oneLineLabel}</strong> has already been created</div>
          <a href={path}>View Voucher</a>
        </div>
      </Alert>
    );
  }

  renderSubmitButton() {
    const { listing, pendingRequest } = this.state;
    const { listings } = this.props;
    let listingVoucher;

    if (listings == null) {
      return;
    }

    if (listings.length == 1 && listings[0].voucher && listings[0].voucher.path) {
      listingVoucher = listings[0].voucher;
    }

    if (listing && listing.voucher && listing.voucher.path) {
      listingVoucher = listing.voucher;
    }

    const voucher = this.state.voucher || listingVoucher;

    if (voucher) {
      return (
        <Button label="View Voucher" name="view-voucher-button" onClick={() => window.location = voucher.path}/>
      );
    } else {
      const disabled = pendingRequest || (!listing && listings.length > 1);

      return (
        <Button disabled={disabled} name="create-voucher-button" onClick={this.handleCreateVoucher}>
          Create Voucher
        </Button>
      );
    }
  }

  renderPickerBody(listings) {
    const { listing } = this.state;
    let label, getLabel;

    if (listings && listings[0].type == 'LotListing') {
      label = 'Lot';
      getLabel = listing => listing.number;
    } else if (listings && listings[0].type == 'CondoListing') {
      label = 'Condo';
      getLabel = listing => listing.address2 || listing.number;
    } else {
      label = 'Lease Space';
      getLabel = listing => listing.address2;
    }

    const options = listings.map(listing => (
      <option key={listing.id} value={listing.id}>
        {getLabel(listing)}
      </option>
    ));

    return (
      <ManagedFormGroup
        includeBlank
        label={label}
        name="listing-select"
        type="select"
        value={listing && listing.id}
        onValueChange={this.handleValueChange}
      >
        {options}
      </ManagedFormGroup>
    );
  }

  renderVoucherCreatedBody(voucher) {
    return (
      <div name="creation-results">
        <p>A voucher for <b>{voucher.oneLineLabel}</b> was successfully created.</p>
        <p>
          Voucher status:
          <Badge bg={voucher.statusPillClass} className="ms-2">
            {voucher.statusLabel}
          </Badge>
        </p>
      </div>
    );
  }

  renderConfirmationBody(listing) {
    const { voucher } = listing;
    const label = <strong>{voucher.oneLineLabel || ''}</strong>;

    return (
      <p name="confirmation">
        Are you sure you want to create a new {toLower(listing.typeLabel)} voucher for {label}?
      </p>
    );
  }
}
